import { motion } from 'framer-motion'
import { useRef, useEffect } from 'react'
import { useSimStore } from '../store/simStore'
import { useSimClock } from '../hooks/useSimClock'
import { familyColour } from '../lib/colours'

const ROW_H = 22
const LABEL_W = 80

/** Invert on-shift windows → off-shift gaps within [0, horizon]. */
function offShiftWindows(
  schedules: Record<string, { on_windows: [number, number][] }>,
  horizon: number,
): [number, number][] {
  const merged: [number, number][] = Object.values(schedules)
    .flatMap((s) => s.on_windows as [number, number][])
    .sort((a, b) => a[0] - b[0])

  const gaps: [number, number][] = []
  let cursor = 0
  for (const [start, end] of merged) {
    if (start > cursor) gaps.push([cursor, start])
    cursor = Math.max(cursor, end)
  }
  if (cursor < horizon) gaps.push([cursor, horizon])
  return gaps
}

export function GanttPanel() {
  const { instanceConfig, activeJobs, completedOrders, startedOrders } = useSimStore()
  const simTime = useSimClock()
  const scrollRef = useRef<HTMLDivElement>(null)

  if (!instanceConfig) return null

  const horizon = Math.max(1, instanceConfig.horizon)
  const schedules = instanceConfig.work_schedules

  const activeOrderIds    = new Set(activeJobs.map((j) => j.order_id))
  const startedOrderIds   = new Set(startedOrders.keys())

  // Active/in-progress first, then completed, within each group sort by release_date
  const orders = [...instanceConfig.orders].sort((a, b) => {
    const aActive    = activeOrderIds.has(a.order_id) || (startedOrderIds.has(a.order_id) && !completedOrders.has(a.order_id))
    const bActive    = activeOrderIds.has(b.order_id) || (startedOrderIds.has(b.order_id) && !completedOrders.has(b.order_id))
    const aCompleted = completedOrders.has(a.order_id)
    const bCompleted = completedOrders.has(b.order_id)
    // Priority: active > not-yet-started > completed
    const aPriority  = aActive ? 0 : aCompleted ? 2 : 1
    const bPriority  = bActive ? 0 : bCompleted ? 2 : 1
    if (aPriority !== bPriority) return aPriority - bPriority
    return a.release_date !== b.release_date ? a.release_date - b.release_date : a.due_date - b.due_date
  })

  // svgW: roughly 1 px per sim-minute, min 800
  const svgW = Math.max(800, LABEL_W + horizon)
  const toX = (t: number) => LABEL_W + (t / horizon) * (svgW - LABEL_W)

  const offWindows = offShiftWindows(schedules, horizon)
  const svgH = Math.max(1, orders.length) * ROW_H

  // Auto-scroll: keep the progress line centered in the visible area
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const progressX = LABEL_W + (simTime / horizon) * (svgW - LABEL_W)
    const target = progressX - el.clientWidth / 2
    el.scrollLeft = Math.max(0, target)
  }, [simTime, horizon, svgW])

  // Time-axis ticks: every major shift boundary (multiples of 480)
  const tickInterval = 480
  const ticks: number[] = []
  for (let t = 0; t <= horizon; t += tickInterval) ticks.push(t)

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
      className="absolute bottom-0 left-0 right-64 h-52 bg-[#0d1117]/90 border-t border-[#30363d] backdrop-blur-sm z-10 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center px-3 py-1 border-b border-[#21262d] text-xs text-slate-400">
        <span className="font-bold text-teal-400">WORK-ORDER GANTT</span>
        <span className="ml-4 text-slate-500">{orders.length} orders · scroll →</span>
        {/* Legend */}
        <span className="ml-auto flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ background: '#2dd4bf' }} />
            <span className="text-slate-500">active</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm opacity-55" style={{ background: '#94a3b8' }} />
            <span className="text-slate-500">done</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-[#10b981]">▷</span>
            <span className="text-slate-500">avail</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-[#f43f5e]">◆</span>
            <span className="text-slate-500">due</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm opacity-60" style={{ background: '#f43f5e' }} />
            <span className="text-slate-500">overrun</span>
          </span>
        </span>
      </div>

      <div ref={scrollRef} className="overflow-y-auto overflow-x-auto" style={{ height: 'calc(100% - 26px)' }}>
        <svg width={svgW} height={svgH + 18} className="block">

          {/* ── Off-shift shading ── */}
          {offWindows.map(([s, e], i) => (
            <rect
              key={i}
              x={toX(s)} y={0}
              width={Math.max(0, toX(e) - toX(s))} height={svgH}
              fill="#1c1f26" opacity={0.6}
            />
          ))}

          {/* ── Time axis ticks ── */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={toX(t)} y1={0}
                x2={toX(t)} y2={svgH}
                stroke="#21262d" strokeWidth={1}
              />
              <text
                x={toX(t) + 2} y={svgH + 13}
                fill="#4b5563" fontSize={7}
                fontFamily="JetBrains Mono, monospace"
              >
                {t}
              </text>
            </g>
          ))}

          {orders.map((order, idx) => {
            const y = idx * ROW_H
            const activeJob = activeJobs.find((j) => j.order_id === order.order_id)
            const completedInfo = completedOrders.get(order.order_id)
            const isCompleted = !!(completedInfo && completedInfo.sim_time <= simTime)
            const colour = familyColour(order.fg_material)

            // Release: show the actual order release_date (when the order becomes available)
            const releaseX = toX(order.release_date)
            const dueDateX = toX(order.due_date)
            const midY = y + ROW_H / 2

            // Actual processing start (from sim data; 0 = not yet started).
            // startedOrders acts as persistent memory so the fill never disappears
            // while an order is in transit between routing steps (not on any machine).
            const rawActualStart =
              activeJob?.actual_start
              ?? completedInfo?.actual_start
              ?? startedOrders.get(order.order_id)
              ?? 0
            const actualStart = rawActualStart > 0 ? rawActualStart : null
            const isInTransit = !activeJob && !isCompleted && actualStart !== null

            // isLate: completed orders use backend flag; in-progress: late if sim clock passed due date
            const isLate = completedInfo?.is_late
              ?? (simTime > order.due_date && (!!activeJob || isInTransit))

            return (
              <g key={order.order_id}>
                {/* Label */}
                <text
                  x={LABEL_W - 4}
                  y={midY + 4}
                  textAnchor="end"
                  fontSize={8}
                  fontFamily="JetBrains Mono, monospace"
                  fill={isCompleted && !isLate ? '#10b981' : isLate ? '#f43f5e' : '#94a3b8'}
                >
                  {order.order_id.slice(-6)}{isLate && isCompleted ? ' !' : isLate ? ' ⚠' : isCompleted ? ' ✓' : ''}
                </text>

                {/* Background bar: release_date → due_date */}
                <rect
                  x={releaseX}
                  y={y + 4}
                  width={Math.max(0, dueDateX - releaseX)}
                  height={ROW_H - 8}
                  fill="#21262d"
                  rx={2}
                />

                {/* Processing fill — split into on-time portion and overrun (past due_date) */}
                {actualStart !== null && (() => {
                  const endTime  = isCompleted ? completedInfo!.sim_time : simTime
                  const opacity  = isCompleted ? 0.50 : isInTransit ? 0.45 : 0.80
                  const onTimeW  = Math.max(0, Math.min(toX(endTime), dueDateX) - toX(actualStart))
                  const overrunX = dueDateX
                  const overrunW = Math.max(0, toX(endTime) - dueDateX)
                  return (
                    <>
                      {/* On-time segment */}
                      {onTimeW > 0 && (
                        <rect x={toX(actualStart)} y={y + 4}
                          width={onTimeW} height={ROW_H - 8}
                          fill={colour} opacity={opacity} rx={2}
                        />
                      )}
                      {/* Overrun segment (past due date) — red tint */}
                      {overrunW > 0 && (
                        <rect x={overrunX} y={y + 4}
                          width={overrunW} height={ROW_H - 8}
                          fill="#f43f5e" opacity={isCompleted ? 0.35 : 0.55} rx={2}
                        />
                      )}
                    </>
                  )
                })()}

                {/* Active leading-edge highlight */}
                {activeJob && !isCompleted && actualStart !== null && (
                  <rect
                    x={toX(simTime) - 3}
                    y={y + 3}
                    width={3}
                    height={ROW_H - 6}
                    fill={colour}
                    opacity={0.95}
                    rx={1}
                  />
                )}

                {/* Completion end-bar */}
                {isCompleted && (
                  <rect
                    x={toX(completedInfo!.sim_time) - 2}
                    y={y + 2}
                    width={2}
                    height={ROW_H - 4}
                    fill={completedInfo!.is_late ? '#f43f5e' : '#10b981'}
                    opacity={0.95}
                  />
                )}

                {/* Release triangle ▷ at release_date */}
                <polygon
                  points={`${releaseX},${y + 3} ${releaseX + 5},${midY} ${releaseX},${y + ROW_H - 3}`}
                  fill={colour}
                  opacity={0.85}
                />

                {/* Due-date diamond ◆ */}
                {dueDateX <= svgW && (
                  <rect
                    x={dueDateX - 4}
                    y={midY - 4}
                    width={7}
                    height={7}
                    transform={`rotate(45 ${dueDateX} ${midY})`}
                    fill={isLate ? '#f43f5e' : '#f59e0b'}
                    opacity={0.9}
                  />
                )}
              </g>
            )
          })}

          {/* Time cursor — on top */}
          <line
            x1={toX(simTime)} y1={0}
            x2={toX(simTime)} y2={svgH}
            stroke="#2dd4bf" strokeWidth={1.5} strokeDasharray="4 2" opacity={0.9}
          />
        </svg>
      </div>
    </motion.div>
  )
}
